import { Box, HStack, Img, Text, VStack } from '@chakra-ui/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useContext, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AuthContext } from '../hooks/auth/useAuth'
import { useCurrentSelf } from '../hooks/auth/useCurrentSelf'
import { useRole } from '../hooks/auth/useRole'
import { useFeatureGate } from '../hooks/useFeatureGate'
import { currentSelfQueryOptions, myPermissionsQueryOptions } from '../lib/performance/queryOptions'
import { prefetchPathIntent } from '../lib/performance/prefetch'
import { resolvePlanningPath } from '../lib/navigation/navigationConfig'
import { checkInCapabilities } from '../pages/OfficeCheckIn/api'

import LogoIcon from '../assets/wrk_logo.png'
import { useTranslation } from 'react-i18next'
import {
  House,
  Bell,
  Users,
  Gauge,
  Calendar,
  Wallet,
  Briefcase,
  ClipboardList,
  Compass,
  Settings,
  User,
  Building2,
  HelpCircle,
  Sparkles,
  BarChart3,
  ArrowLeftRight,
  ClipboardCheck,
  Library,
  Ticket,
  type LucideIcon,
} from 'lucide-react'

interface NavItemConfig {
  icon: LucideIcon
  title: string
  path: string
  activeRoute: string
  /** Additional route families represented by the same navigation item. */
  activeRoutes?: string[]
  isDisabled: boolean
  isHighlight?: boolean
  /** If set, this nav item is only visible when the org's plan includes this feature */
  featureKey?: string
}

interface NavSection {
  label: string
  items: NavItemConfig[]
}

interface NavItemProps {
  icon: LucideIcon
  title: string
  path: string
  isDisabled: boolean
  selected: boolean
  isHighlight?: boolean
  handleClick: (path: string) => void
  handleIntent: (path: string) => void
}

const NavItem: React.FC<NavItemProps> = ({
  icon: IconComponent,
  title,
  path,
  isDisabled,
  selected,
  isHighlight,
  handleClick,
  handleIntent,
}) => {
  if (isHighlight) {
    return (
      <HStack
        spacing="10px"
        py="9px"
        px="12px"
        cursor="pointer"
        onClick={() => handleClick(path)}
        onMouseEnter={() => handleIntent(path)}
        onFocus={() => handleIntent(path)}
        bg={selected ? '#D0DDFF4D' : 'transparent'}
        borderRadius="10px"
        transition="all 0.2s ease-in-out"
        _hover={{ bg: '#D0DDFF4D' }}
        css={{
          '& > svg, & > p': {
            background: 'linear-gradient(135deg, #6B67D4 0%, #9B6DFF 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          },
          '& > svg': {
            stroke: '#7B6FE0',
            WebkitTextFillColor: 'initial',
            background: 'none',
          },
        }}
      >
        <IconComponent size={19} strokeWidth={selected ? 2.2 : 2} />
        <Text fontSize="14px" fontWeight="600" lineHeight="20px">
          {title}
        </Text>
      </HStack>
    )
  }

  return (
    <HStack
      spacing="10px"
      py="9px"
      px="12px"
      height="40px"
      _hover={{ backgroundColor: isDisabled ? undefined : '#D0DDFF4D' }}
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      color={selected ? 'primary.100' : 'solid.900'}
      onClick={() => !isDisabled && handleClick(path)}
      onMouseEnter={() => !isDisabled && handleIntent(path)}
      onFocus={() => !isDisabled && handleIntent(path)}
      backgroundColor={selected ? '#D0DDFF4D' : 'transparent'}
      borderRadius="10px"
      transition="all 0.2s ease-in-out"
    >
      <IconComponent size={19} strokeWidth={selected ? 2.2 : 2} />
      <Text fontSize="14px" fontWeight={selected ? '600' : '500'} lineHeight="20px">
        {title}
      </Text>
    </HStack>
  )
}

const SectionHeader: React.FC<{ label: string }> = ({ label }) => (
  <Text
    fontSize="10.5px"
    fontWeight="600"
    color="solid.400"
    textTransform="uppercase"
    letterSpacing="0.6px"
    px="12px"
    mt="14px"
    mb="2px"
  >
    {label}
  </Text>
)

const isNavRouteSelected = (pathname: string, item: NavItemConfig) => {
  const activeRoutes = item.activeRoutes ?? (item.activeRoute ? [item.activeRoute] : [])
  if (!activeRoutes.length) return false
  if (activeRoutes.includes('/user/explore')) {
    return pathname.startsWith('/user/explore') || pathname.startsWith('/user/content')
  }
  return activeRoutes.some((route) => pathname.startsWith(route))
}

const NavigationBar: React.FC<{ isInDrawer?: boolean; onNavigate?: () => void }> = ({ isInDrawer = false, onNavigate }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const location = useLocation()
  const navRef = useRef<HTMLDivElement>(null)
  const auth = useContext(AuthContext)
  const user = auth?.user
  const { data: checkIn } = useQuery({
    queryKey: ['office-checkin-capabilities', user?.id, user?.organization_id],
    queryFn: checkInCapabilities, enabled: !!user, staleTime: 60000, retry: false,
  })
  const [mailtoClicked, setMailtoClicked] = useState(false)
  const { isAdmin, isOrgAdmin, isSuperAdmin, role: currentRole } = useRole()

  useCurrentSelf(!!user)

  const { data: myPermissions } = useQuery(
    myPermissionsQueryOptions,
  )

  const { hasFeature } = useFeatureGate()

  const hasPlanningAccess = (myPermissions?.planning?.length ?? 0) > 0
  const hasWorkforceAccess = (myPermissions?.workforce?.length ?? 0) > 0
  const hasInflowAccess = (myPermissions?.inflow?.length ?? 0) > 0
  const hasExploreAccess = (myPermissions?.explore?.length ?? 0) > 0
  // Content is always visible to org/super admins, independent of PagePermission
  // config — mirrors the guarantee already given to super_admin/org_admin by the
  // my-permissions endpoint (their unconfigured sections auto-default to open).
  const hasLibraryAccess =
    isOrgAdmin || isSuperAdmin || (myPermissions?.library?.length ?? 0) > 0
  // Resolve first available planning tab for nav link
  const planningPath = (() => {
    const allowed = myPermissions?.planning || []
    return resolvePlanningPath(allowed)
  })()

  const hasInflowFeature = isSuperAdmin || hasFeature('inflow')
  const hasOutflowFeature = isSuperAdmin || hasFeature('outflow')
  const canSeeHiring = (isOrgAdmin || isSuperAdmin || hasInflowAccess) && hasInflowFeature
  const canSeeDepartures = isAdmin && hasOutflowFeature
  const peopleFlowPath = canSeeHiring ? '/user/inflow/candidates' : '/user/outflow/offboarding'

  const peopleFlowItem: NavItemConfig = {
    icon: ArrowLeftRight,
    title: t('navigation.peopleFlow', 'Flows'),
    path: peopleFlowPath,
    activeRoute: '/user/inflow',
    activeRoutes: ['/user/inflow', '/user/outflow'],
    isDisabled: false,
  }

  if (!user) return null

  // --- Bottom-pinned items (always visible) ---
  const aiAssistantItem: NavItemConfig = {
    icon: Sparkles,
    title: t('navigation.aiAssistant', 'AI Assistant'),
    path: '/user/chat',
    activeRoute: '/user/chat',
    isDisabled: false,
    isHighlight: true,
    featureKey: 'ai_assistant',
  }

  // Product-ticket tracker. super_admin sees every org's tickets merged; org_admin sees
  // only their own org's. Both roles can view/add/edit — gated below in visibleBottomItems.
  const ticketsItem: NavItemConfig = {
    icon: Ticket,
    title: t('navigation.tickets', 'Tickets'),
    path: '/user/tickets',
    activeRoute: '/user/tickets',
    isDisabled: false,
  }

  // Assessments used to be a tab inside Explore, so only people with the
  // 'assessments' grant could reach it. It has its own entry now; the same
  // grant decides whether the entry is there at all.
  const canSeeAssessments =
    isOrgAdmin || isSuperAdmin || (myPermissions?.explore ?? []).includes('assessments')
  const assessmentsItems: NavItemConfig[] = canSeeAssessments
    ? [
        {
          icon: ClipboardList,
          title: t('navigation.assessments', 'Assessments'),
          path: '/user/assessments',
          activeRoute: '/user/assessments',
          isDisabled: false,
          featureKey: 'assessments',
        },
      ]
    : []

  const bottomItems: NavItemConfig[] = [
    {
      icon: HelpCircle,
      title: t('navigation.help', 'Help'),
      path: '/user/help',
      activeRoute: '/user/help',
      isDisabled: false,
    },
    {
      icon: Settings,
      title: t('navigation.settings'),
      path: ['super_admin', 'org_admin'].includes(currentRole || '')
        ? '/user/settings/general'
        : '/user/profile?tab=settings',
      activeRoute: ['super_admin', 'org_admin'].includes(currentRole || '') ? '/user/settings' : '',
      isDisabled: false,
    },
  ]

  // --- Section configs per role ---
  const sectionsOrgAdmin: NavSection[] = [
    {
      label: t('navigation.sectionMain', 'Main'),
      items: [
        { icon: House, title: t('navigation.home'), path: '/user/home', activeRoute: '/user/home', isDisabled: false },
        {
          icon: Bell,
          title: t('navigation.todo'),
          path: '/user/todo',
          activeRoute: '/user/todo',
          isDisabled: false,
        },
      ],
    },

    {
      label: t('navigation.sectionPeople', 'People'),
      items: [
        {
          icon: Users,
          title: t('navigation.workforce'),
          path: '/user/workforce/people',
          activeRoute: '/user/workforce',
          isDisabled: false,
          featureKey: 'workforce',
        },
        {
          icon: Gauge,
          title: t('navigation.evaluations'),
          path: '/user/evaluations',
          activeRoute: '/user/evaluations',
          isDisabled: false,
          featureKey: 'evaluations',
        },
        ...(canSeeHiring || canSeeDepartures ? [peopleFlowItem] : []),
        ...assessmentsItems,
        {
          icon: BarChart3,
          title: t('navigation.analytics', 'Analytics'),
          path: '/user/analytics/workforce',
          activeRoute: '/user/analytics',
          isDisabled: false,
          featureKey: 'analytics',
        },
      ],
    },
    {
      label: t('navigation.sectionManage', 'Manage'),
      items: [
        {
          icon: Calendar,
          title: t('navigation.planning'),
          path: planningPath,
          activeRoute: '/user/planning',
          isDisabled: false,
          featureKey: 'scheduling',
        },
        {
          icon: Wallet,
          title: t('navigation.payroll'),
          path: '/user/payroll/people',
          activeRoute: '/user/payroll',
          activeRoutes: ['/user/payroll', '/user/expenses'],
          isDisabled: false,
          featureKey: 'payroll',
        },
        {
          icon: ClipboardCheck,
          title: t('navigation.assign', 'Assign'),
          path: '/user/assign/document',
          activeRoute: '/user/assign',
          isDisabled: false,
        },
        ...(hasExploreAccess
          ? [
              {
                icon: Compass,
                title: t('navigation.explore'),
                path: '/user/explore/content',
                activeRoute: '/user/explore',
                isDisabled: false,
                featureKey: 'explore',
              },
            ]
          : []),
        {
          icon: Briefcase,
          title: t('navigation.classifications'),
          path: '/user/classification/jobprofile',
          activeRoute: '/user/classification',
          isDisabled: false,
          featureKey: 'classifications',
        },
        //      {
        //       icon: Wallet,
        //        title: t('Payments'),
        //        path: '/user/settings/payments',
        //        activeRoute: '/user/settings/payments',
        //        isDisabled: false,
        //      },
      ],
    },
    {
      label: t('navigation.sectionContent', 'Content'),
      items: [
        ...(hasLibraryAccess
          ? [
              {
                icon: Library,
                title: t('navigation.content', 'Content'),
                path: '/user/library/documents',
                activeRoute: '/user/library',
                isDisabled: false,
              },
            ]
          : []),
      ],
    },
  ]

  const sectionsCountryAdmin: NavSection[] = [
    {
      label: t('navigation.sectionMain', 'Main'),
      items: [
        { icon: House, title: t('navigation.home'), path: '/user/home', activeRoute: '/user/home', isDisabled: false },
      ],
    },
    {
      label: t('navigation.sectionPeople', 'People'),
      items: [
        ...(hasWorkforceAccess
          ? [
              {
                icon: Users,
                title: t('navigation.workforce'),
                path: '/user/workforce/people',
                activeRoute: '/user/workforce',
                isDisabled: false,
                featureKey: 'workforce',
              },
            ]
          : []),
        {
          icon: Gauge,
          title: t('navigation.evaluations'),
          path: '/user/evaluations',
          activeRoute: '/user/evaluations',
          isDisabled: false,
        },
        ...(canSeeHiring || canSeeDepartures ? [peopleFlowItem] : []),
        ...assessmentsItems,
        {
          icon: BarChart3,
          title: t('navigation.analytics', 'Analytics'),
          path: '/user/analytics/workforce',
          activeRoute: '/user/analytics',
          isDisabled: false,
        },
      ],
    },
    {
      label: t('navigation.sectionManage', 'Manage'),
      items: [
        ...(hasPlanningAccess
          ? [
              {
                icon: Calendar,
                title: t('navigation.planning'),
                path: planningPath,
                activeRoute: '/user/planning',
                isDisabled: false,
              } as NavItemConfig,
            ]
          : []),
        { icon: ClipboardCheck, title: t('navigation.assign', 'Assign'), path: '/user/assign/document', activeRoute: '/user/assign', isDisabled: false },
        ...(hasExploreAccess
          ? [
              {
                icon: Compass,
                title: t('navigation.explore'),
                path: '/user/explore/content',
                activeRoute: '/user/explore',
                isDisabled: false,
                featureKey: 'explore',
              },
            ]
          : []),
        { icon: Briefcase, title: t('navigation.classifications'), path: '/user/classification/jobprofile', activeRoute: '/user/classification', isDisabled: false, featureKey: 'classifications' },
      ],
    },
    {
      label: t('navigation.sectionContent', 'Content'),
      items: [
        ...(hasLibraryAccess
          ? [
              {
                icon: Library,
                title: t('navigation.content', 'Content'),
                path: '/user/library/documents',
                activeRoute: '/user/library',
                isDisabled: false,
              },
            ]
          : []),
      ],
    },
  ]

  const sectionsFinance: NavSection[] = [
    {
      label: t('navigation.sectionMain', 'Main'),
      items: [
        { icon: House, title: t('navigation.home'), path: '/user/home', activeRoute: '/user/home', isDisabled: false },
      ],
    },
    {
      label: t('navigation.sectionPeople', 'People'),
      items: [
        ...(hasWorkforceAccess
          ? [
              {
                icon: Users,
                title: t('navigation.workforce'),
                path: '/user/workforce/people',
                activeRoute: '/user/workforce',
                isDisabled: false,
                featureKey: 'workforce',
              },
            ]
          : []),
        ...(canSeeHiring || canSeeDepartures ? [peopleFlowItem] : []),
        ...assessmentsItems,
        {
          icon: BarChart3,
          title: t('navigation.analytics', 'Analytics'),
          path: '/user/analytics/workforce',
          activeRoute: '/user/analytics',
          isDisabled: false,
        },
      ],
    },
    {
      label: t('navigation.sectionManage', 'Manage'),
      items: [
        ...(hasPlanningAccess
          ? [
              {
                icon: Calendar,
                title: t('navigation.planning'),
                path: planningPath,
                activeRoute: '/user/planning',
                isDisabled: false,
              } as NavItemConfig,
            ]
          : []),
        {
          icon: Wallet,
          title: t('navigation.payroll'),
          path: '/user/payroll/people',
          activeRoute: '/user/payroll',
          activeRoutes: ['/user/payroll', '/user/expenses'],
          isDisabled: false,
        },
        { icon: ClipboardCheck, title: t('navigation.assign', 'Assign'), path: '/user/assign/document', activeRoute: '/user/assign', isDisabled: false },
        ...(hasExploreAccess
          ? [
              {
                icon: Compass,
                title: t('navigation.explore'),
                path: '/user/explore/content',
                activeRoute: '/user/explore',
                isDisabled: false,
                featureKey: 'explore',
              },
            ]
          : []),
        { icon: Briefcase, title: t('navigation.classifications'), path: '/user/classification/jobprofile', activeRoute: '/user/classification', isDisabled: false, featureKey: 'classifications' },
      ],
    },
    {
      label: t('navigation.sectionContent', 'Content'),
      items: [
        ...(hasLibraryAccess
          ? [
              {
                icon: Library,
                title: t('navigation.content', 'Content'),
                path: '/user/library/documents',
                activeRoute: '/user/library',
                isDisabled: false,
              },
            ]
          : []),
      ],
    },
  ]

  const sectionsTeamLeader: NavSection[] = [
    {
      label: t('navigation.sectionMain', 'Main'),
      items: [
        { icon: House, title: t('navigation.home'), path: '/user/home', activeRoute: '/user/home', isDisabled: false },
      ],
    },
    {
      label: t('navigation.sectionPeople', 'People'),
      items: [
        ...(hasWorkforceAccess
          ? [
              {
                icon: Users,
                title: t('navigation.workforce'),
                path: '/user/workforce/people',
                activeRoute: '/user/workforce',
                isDisabled: false,
                featureKey: 'workforce',
              },
            ]
          : []),
        ...(canSeeHiring || canSeeDepartures ? [peopleFlowItem] : []),
        ...assessmentsItems,
      ],
    },
    {
      label: t('navigation.sectionManage', 'Manage'),
      items: [
        ...(hasPlanningAccess
          ? [
              {
                icon: Calendar,
                title: t('navigation.planning'),
                path: planningPath,
                activeRoute: '/user/planning',
                isDisabled: false,
              } as NavItemConfig,
            ]
          : []),
        { icon: ClipboardCheck, title: t('navigation.assign', 'Assign'), path: '/user/assign/document', activeRoute: '/user/assign', isDisabled: false },
        { icon: Briefcase, title: t('navigation.classifications'), path: '/user/classification/jobprofile', activeRoute: '/user/classification', isDisabled: false, featureKey: 'classifications' },
        ...(hasExploreAccess
          ? [
              {
                icon: Compass,
                title: t('navigation.explore'),
                path: '/user/explore/content',
                activeRoute: '/user/explore',
                isDisabled: false,
                featureKey: 'explore',
              },
            ]
          : []),
      ],
    },
    {
      label: t('navigation.sectionContent', 'Content'),
      items: [
        ...(hasLibraryAccess
          ? [
              {
                icon: Library,
                title: t('navigation.content', 'Content'),
                path: '/user/library/documents',
                activeRoute: '/user/library',
                isDisabled: false,
              },
            ]
          : []),
      ],
    },
  ]

  const sectionsUser: NavSection[] = [
    {
      label: t('navigation.sectionMain', 'Main'),
      items: [
        { icon: House, title: t('navigation.home'), path: '/user/home', activeRoute: '/user/home', isDisabled: false },
        {
          icon: User,
          title: t('navigation.myProfile'),
          path: '/user/profile',
          activeRoute: '/user/profile',
          isDisabled: false,
        },
      ],
    },
    {
      label: t('navigation.sectionManage', 'Manage'),
      items: [
        {
          icon: Building2,
          title: t('navigation.organization'),
          path: '/user/organization',
          activeRoute: '/user/organization',
          isDisabled: false,
        },
        ...(hasPlanningAccess
          ? [
              {
                icon: Calendar,
                title: t('navigation.planning'),
                path: planningPath,
                activeRoute: '/user/planning',
                isDisabled: false,
              } as NavItemConfig,
            ]
          : []),
        ...(hasExploreAccess
          ? [
              {
                icon: Compass,
                title: t('navigation.explore'),
                path: '/user/explore/content',
                activeRoute: '/user/explore',
                isDisabled: false,
                featureKey: 'explore',
              },
            ]
          : []),
        ...assessmentsItems,
      ],
    },
    {
      label: t('navigation.sectionContent', 'Content'),
      items: [
        ...(hasLibraryAccess
          ? [
              {
                icon: Library,
                title: t('navigation.content', 'Content'),
                path: '/user/library/documents',
                activeRoute: '/user/library',
                isDisabled: false,
              },
            ]
          : []),
      ],
    },
  ]

  // Select sections based on role
  let sections: NavSection[]
  if (!isAdmin) {
    sections = sectionsUser.filter((section) => section.label !== t('navigation.sectionContent', 'Content'))
  } else {
    switch (currentRole) {
      case 'super_admin':
      case 'org_admin':
        sections = sectionsOrgAdmin
        break
      case 'country_admin':
        sections = sectionsCountryAdmin
        break
      case 'finance':
        sections = sectionsFinance
        break
      case 'team_leader':
        sections = sectionsTeamLeader
        break
      default:
        sections = sectionsUser
        break
    }
  }

  // Content is a management tool, so keep it with the other day-to-day tools
  if (checkIn?.enabled) {
    sections = sections.map((section, index) => index === 0 ? { ...section, items: [...section.items,
      { icon: Building2, title: 'Office check-in', path: '/user/office-checkin', activeRoute: '/user/office-checkin', isDisabled: false },
    ] } : section)
  }

  // instead of giving a single link its own sidebar section.
  const libraryItems = isAdmin ? sections
    .filter((section) => section.label === t('navigation.sectionContent', 'Content'))
    .flatMap((section) => section.items) : []
  if (libraryItems.length > 0) {
    sections = sections
      .filter((section) => section.label !== t('navigation.sectionContent', 'Content'))
      .map((section) => section.label === t('navigation.sectionManage', 'Manage')
        ? { ...section, items: [...section.items, ...libraryItems] }
        : section)
  }

  if (isInDrawer) {
    // Explore used to live in its own "Learn" section, hidden in the mobile drawer to keep it
    // compact; now that it's merged into Manage, strip just that item instead of a whole section.
    sections = sections.map((section) =>
      section.label === t('navigation.sectionManage', 'Manage')
        ? { ...section, items: section.items.filter((item) => item.title !== t('navigation.explore')) }
        : section,
    )
  }

  // Filter nav items by subscription features (super_admin always sees all)
  if (currentRole !== 'super_admin') {
    sections = sections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => !item.featureKey || hasFeature(item.featureKey)),
      }))
      .filter((section) => section.items.length > 0)
  }

  let visibleBottomItems = ['super_admin', 'org_admin'].includes(currentRole || '')
    ? bottomItems
    : bottomItems.filter((item) => item.path !== '/user/settings/general')

  if (['super_admin', 'org_admin'].includes(currentRole || '')) {
    visibleBottomItems = [ticketsItem, ...visibleBottomItems]
  }

  if (currentRole !== 'super_admin') {
    visibleBottomItems = visibleBottomItems.filter((item) => !item.featureKey || hasFeature(item.featureKey))
  }

  const handleNavigation = (path: string) => {
    if (path.startsWith('mailto:')) {
      setMailtoClicked(true)
      window.open(path, '_blank')
      setTimeout(() => setMailtoClicked(false), 300)
    } else {
      navigate(path)
    }
    onNavigate?.()
  }

  const handleIntent = (path: string) => {
    prefetchPathIntent(path, queryClient)
    if (
      path.startsWith('/user/planning') ||
      path.startsWith('/user/workforce') ||
      path.startsWith('/user/inflow') ||
      path.startsWith('/user/outflow')
    ) {
      void queryClient.prefetchQuery(currentSelfQueryOptions(queryClient, !!user))
    }
  }

  return (
    <Box
      ref={navRef}
      h={isInDrawer ? '100%' : '100vh'}
      backgroundColor="background.White"
      display={isInDrawer ? 'flex' : { base: 'none', md: 'flex' }}
      flexDirection="column"
      width={isInDrawer ? '100%' : '230px'}
      minW={isInDrawer ? undefined : '230px'}
      borderRight={isInDrawer ? 'none' : '1px solid'}
      borderRightColor="solid.50"
      px="12px"
      py="16px"
    >
      {/* Logo */}
      <Box mt="8px" mb="8px" display="flex" justifyContent="center">
        <Img src={LogoIcon} alt="Logo" width="100px" height="50px" />
      </Box>

      {/* Scrollable sections */}
      <VStack
        flex="1"
        align="stretch"
        spacing="2px"
        overflowY="auto"
        css={{
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.08)', borderRadius: '2px' },
        }}
      >
        {sections.map((section) => (
          <Box key={section.label}>
            <SectionHeader label={section.label} />
            {section.items.map((item) => (
              <NavItem
                key={item.path}
                icon={item.icon}
                title={item.title}
                path={item.path}
                isDisabled={item.isDisabled}
                selected={isNavRouteSelected(location.pathname, item)}
                handleClick={handleNavigation}
                handleIntent={handleIntent}
              />
            ))}
          </Box>
        ))}
      </VStack>

      <Box pt="4px">
        <NavItem
          icon={aiAssistantItem.icon}
          title={aiAssistantItem.title}
          path={aiAssistantItem.path}
          isDisabled={aiAssistantItem.isDisabled}
          isHighlight={aiAssistantItem.isHighlight}
          selected={isNavRouteSelected(location.pathname, aiAssistantItem)}
          handleClick={handleNavigation}
          handleIntent={handleIntent}
        />
      </Box>

      {/* Bottom-pinned items */}
      <Box borderTop="1px solid" borderColor="solid.50" pt="8px" mt="4px">
        {visibleBottomItems.map((item) => (
          <NavItem
            key={item.path}
            icon={item.icon}
            title={item.title}
            path={item.path}
            isDisabled={item.isDisabled}
            isHighlight={item.isHighlight}
            selected={item.path.startsWith('mailto:') ? mailtoClicked : isNavRouteSelected(location.pathname, item)}
            handleClick={handleNavigation}
            handleIntent={handleIntent}
          />
        ))}
      </Box>
    </Box>
  )
}

export default NavigationBar
